import { useMutation } from '@apollo/client';
import PriceField from '@website/components/forms/fields/PriceField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import {
  AddUserCreditsDocument,
  GlobalCurrencies,
  UserCreditInput,
} from '@website/types/graphqlOperations';
import { useCallback, useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import currencies from '../../../dataObjects/currencies';

/**
 * List of <option> tags for currencies, to be used
 * inside <select>.
 */
function CurrenciesList() {
  return (
    <>
      {Object.keys(currencies).map((currencyISO) => (
        <option value={currencyISO} key={currencyISO}>
          {currencyISO} ({currencies[currencyISO].symbol_native})
        </option>
      ))}
    </>
  );
}

type Props = {
  userID: string;
  refreshUser: () => void;
};

export function ApplyCreditToUserForm({ userID, refreshUser }: Props) {
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { handleSubmit, control, watch, register, reset } =
    useForm<UserCreditInput>({
      mode: 'onBlur',
      defaultValues: {
        amountZD: 0,
        currency: GlobalCurrencies.USD,
        userID,
      },
    });

  const [addUserCredit, { loading }] = useMutation(AddUserCreditsDocument, {
    onError: (error) => {
      console.log(error);
      setErrorMessage(error.message || 'An unknown error occurred!');
    },
    onCompleted: () => {
      setErrorMessage('');
      setSuccessMessage('Credit applied successfully');
      refreshUser();
      reset();
    },
  });

  const currency = watch('currency');

  const submitForm = useCallback(
    (values: UserCreditInput) => {
      addUserCredit({
        variables: {
          input: values,
        },
      });
    },
    [addUserCredit],
  );

  return (
    <Col className="p-1">
      <Row>
        <Col>
          <h4>Add Credits</h4>
          <p>You can add credits for the user here.</p>
        </Col>
      </Row>
      {errorMessage && (
        <Row>
          <Col>
            <Alert variant="danger">{errorMessage}</Alert>
          </Col>
        </Row>
      )}
      {successMessage && (
        <Row>
          <Col>
            <Alert variant="success">{successMessage}</Alert>
          </Col>
        </Row>
      )}

      <Form onSubmit={handleSubmit(submitForm)}>
        <Row>
          <Col className="pt-3">
            <Row>
              <Col>
                <Form.Group controlId="amount">
                  <Form.Label>Credit Amount</Form.Label>
                  <PriceField
                    name="amountZD"
                    currency={(currency ?? GlobalCurrencies.USD) as any}
                    required
                    control={control}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group controlId="currency">
                  <Form.Label>Credit Currency</Form.Label>
                  <Form.Select
                    name="currency"
                    required
                    {...register('currency', { required: true })}
                  >
                    <CurrenciesList />
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group controlId="note">
                  <Form.Label>Note</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="note"
                    required
                    {...register('note')}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col
                xs="12"
                md="auto"
                style={{ display: 'flex', alignItems: 'flex-end' }}
              >
                <div className="d-grid gap-2 py-2">
                  <SubmitButton block isSubmitting={loading} text="Save" />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </Col>
  );
}
